import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Search,
  RefreshCw,
  Clock,
  CheckCircle,
  AlertCircle,
  Info,
  Check,
  CheckCheck,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useActiveNotifications, useMarkAllAsRead, useMarkAsRead, useUnreadCount } from '@/api-services/hooks/useNotifications';
import DataTablePagination from '@/components/ui/data-table-pagination';
import type { ManageNotificationDTO } from '@/api-services/hooks/notificationTypes';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Helper function to format time since notification
const formatTimeSince = (dateString?: string): string => {
  if (!dateString) return 'Just now';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

// Helper function to format full date
const formatFullDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Helper function to get notification icon and color
const getNotificationIcon = (notification: ManageNotificationDTO) => {
  const priority = notification.priority?.toUpperCase() || 'INFO';
  const messageType = notification.messageType?.toUpperCase() || '';

  if (messageType.includes('ALERT') || priority === 'HIGH') {
    return { icon: <AlertCircle className="w-4 h-4" />, color: 'text-destructive', bg: 'bg-destructive/10' };
  }
  if (messageType.includes('WARNING') || priority === 'MEDIUM') {
    return { icon: <AlertCircle className="w-4 h-4" />, color: 'text-warning', bg: 'bg-warning/10' };
  }
  if (messageType.includes('SUCCESS')) {
    return { icon: <CheckCircle className="w-4 h-4" />, color: 'text-success', bg: 'bg-success/10' };
  }
  return { icon: <Info className="w-4 h-4" />, color: 'text-info', bg: 'bg-info/10' };
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'HIGH' | 'MEDIUM' | 'LOW'>('all');
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed for API
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedNotification, setSelectedNotification] = useState<ManageNotificationDTO | null>(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  // Ref to maintain focus on search input
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchQuery]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length > 2;

  // Fetch active notifications with server-side pagination and search
  const { data: notifications, loading, refetch, totalElements, totalPages } = useActiveNotifications({
    page: currentPage,
    size: itemsPerPage,
    search: shouldSearch ? debouncedSearchTerm : undefined,
    enabled: true,
    onError: (error) => {
      toast.error(`Failed to load notifications: ${error.message}`);
    },
  });

  const { data: unreadCount, refetch: refetchCount } = useUnreadCount(undefined, 'ADMIN', {
    enabled: false,
  });

  const markAsRead = useMarkAsRead({
    onSuccess: () => {
      refetch();
      refetchCount();
    },
    onError: (error) => {
      toast.error(`Failed to mark as read: ${error.message}`);
    },
  });

  const markAllAsRead = useMarkAllAsRead({
    onSuccess: () => {
      refetch();
      refetchCount();
    },
    onError: (error) => {
      toast.error(`Failed to mark as read: ${error.message}`);
    },
  });

  // Client-side filtering for status and priority (search is server-side)
  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];

    return notifications.filter((notification) => {
      if (statusFilter === 'unread' && notification.isRead) return false;
      if (statusFilter === 'read' && !notification.isRead) return false;
      if (priorityFilter !== 'all' && notification.priority !== priorityFilter) return false;
      return true;
    });
  }, [notifications, statusFilter, priorityFilter]);

  // Reset page when filters/search change
  useEffect(() => {
    setCurrentPage(0);
  }, [statusFilter, priorityFilter, debouncedSearchTerm]);

  // Auto-focus search input on page load (after loading completes)
  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  // Handle notification click - mark as read and open detail dialog
  const handleNotificationClick = async (notification: ManageNotificationDTO) => {
    if (!notification.isRead && notification.id) {
      await markAsRead(notification.id);
      // Update the notification object so the dialog shows correct state
      setSelectedNotification({ ...notification, isRead: true });
    } else {
      setSelectedNotification(notification);
    }
  };

  // Navigate to redirectUrl if it's a valid internal admin route
  const handleNavigateToRedirect = (url: string) => {
    setSelectedNotification(null);
    if (url.startsWith('/admin/')) {
      navigate(url);
    } else if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  // Handle mark as read button (single notification)
  const handleMarkAsRead = async (e: React.MouseEvent, notification: ManageNotificationDTO) => {
    e.stopPropagation();
    if (notification.id && !notification.isRead) {
      await markAsRead(notification.id);
      toast.success('Notification marked as read');
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    if (!notifications) return;
    const unreadNotifications = notifications.filter(n => !n.isRead && n.id);
    if (unreadNotifications.length === 0) {
      toast.info('No unread notifications');
      return;
    }

    setMarkingAllRead(true);
    try {
      // await Promise.all(unreadNotifications.map(n => markAsRead(n.id!)));
      await markAllAsRead();
      toast.success(`${unreadNotifications.length} notification${unreadNotifications.length > 1 ? 's' : ''} marked as read`);
    } catch {
      toast.error('Failed to mark some notifications as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  // Stats
  const stats = useMemo(() => {
    if (!notifications) return { total: 0, unread: 0, read: 0 };
    const unread = notifications.filter((n) => !n.isRead).length;
    return {
      total: totalElements,
      unread,
      read: notifications.length - unread,
    };
  }, [notifications, totalElements]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center">
              <Bell className="w-6 h-6 mr-2" />
              Notifications
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your notifications and stay updated
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stats.unread > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={markingAllRead}
            >
              {markingAllRead ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCheck className="w-4 h-4 mr-2" />
              )}
              Mark all as read
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`cursor-pointer transition-colors ${statusFilter === 'all' ? 'bg-muted/50 border-primary' : ''}`}
              onClick={() => setStatusFilter('all')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">All Notifications</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Bell className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-colors ${statusFilter === 'unread' ? 'bg-muted/50 border-primary' : ''}`}
              onClick={() => setStatusFilter('unread')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unread</p>
                <p className="text-2xl font-bold text-warning">{stats.unread}</p>
              </div>
              <Clock className="w-8 h-8 text-warning" />
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-colors ${statusFilter === 'read' ? 'bg-muted/50 border-primary' : ''}`}
              onClick={() => setStatusFilter('read')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Read</p>
                <p className="text-2xl font-bold text-success">{stats.read}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  type="search"
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  autoComplete="off"
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Notifications</SelectItem>
                <SelectItem value="unread">Unread Only</SelectItem>
                <SelectItem value="read">Read Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={(value: any) => setPriorityFilter(value)}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="HIGH">High Priority</SelectItem>
                <SelectItem value="MEDIUM">Medium Priority</SelectItem>
                <SelectItem value="LOW">Low Priority</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {statusFilter === 'unread' && 'Unread '}
            {statusFilter === 'read' && 'Read '}
            Notifications
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({totalElements} total)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-lg font-medium text-foreground">No notifications found</p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'You\'re all caught up!'}
              </p>
            </div>
          ) : (
            <TooltipProvider>
              <div className="space-y-2">
                {filteredNotifications.map((notification) => {
                  const { icon, color, bg } = getNotificationIcon(notification);
                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`flex items-start gap-4 p-4 rounded-lg cursor-pointer transition-all ${
                        !notification.isRead
                          ? 'bg-muted/50 hover:bg-muted border-l-4 border-l-primary'
                          : 'bg-muted/20 hover:bg-muted/30 border-l-4 border-l-transparent'
                      }`}
                    >
                      {/* Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${bg}`}>
                        <div className={color}>{icon}</div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className={`font-semibold text-sm ${!notification.isRead ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {notification.title || 'Notification'}
                              </h4>
                              {!notification.isRead && (
                                <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center">
                                <Clock className="w-3 h-3 mr-1" />
                                {formatTimeSince(notification.createdDate)}
                              </span>
                              {notification.priority && (
                                <Badge variant="outline" className="text-xs">
                                  {notification.priority}
                                </Badge>
                              )}
                              {notification.messageType && (
                                <Badge variant="outline" className="text-xs">
                                  {notification.messageType}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!notification.isRead && notification.id && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                    onClick={(e) => handleMarkAsRead(e, notification)}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Mark as read</TooltipContent>
                              </Tooltip>
                            )}
                            {notification.isRead && (
                              <CheckCircle className="w-4 h-4 text-muted-foreground/40" />
                            )}
                          </div>
                        </div>

                        {/* Full date */}
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatFullDate(notification.createdDate)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </TooltipProvider>
          )}
          {!loading && totalPages > 0 && (
            <DataTablePagination
              currentPage={currentPage + 1}
              totalPages={totalPages}
              totalItems={totalElements}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page - 1)}
              onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(0); }}
              itemsPerPageOptions={[25, 50, 75, 100]}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Notification Detail Dialog */}
      <Dialog open={!!selectedNotification} onOpenChange={(open) => !open && setSelectedNotification(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNotification && (() => {
                const { icon, color } = getNotificationIcon(selectedNotification);
                return <span className={color}>{icon}</span>;
              })()}
              {selectedNotification?.title || 'Notification'}
            </DialogTitle>
          </DialogHeader>
          {selectedNotification && (
            <div className="space-y-4">
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {selectedNotification.message}
              </p>

              <div className="flex flex-wrap gap-2">
                {selectedNotification.priority && (
                  <Badge variant="outline">{selectedNotification.priority}</Badge>
                )}
                {selectedNotification.messageType && (
                  <Badge variant="outline">{selectedNotification.messageType}</Badge>
                )}
                {selectedNotification.isRead ? (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Read
                  </Badge>
                ) : (
                  <Badge className="bg-primary text-primary-foreground">Unread</Badge>
                )}
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <p>Created: {formatFullDate(selectedNotification.createdDate)}</p>
                {selectedNotification.readDate && (
                  <p>Read: {formatFullDate(selectedNotification.readDate)}</p>
                )}
                {selectedNotification.expiryDate && (
                  <p>Expires: {formatFullDate(selectedNotification.expiryDate)}</p>
                )}
              </div>

              <div className="flex gap-2">
                {!selectedNotification.isRead && selectedNotification.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={async () => {
                      await markAsRead(selectedNotification.id!);
                      setSelectedNotification({ ...selectedNotification, isRead: true });
                      toast.success('Notification marked as read');
                    }}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Mark as Read
                  </Button>
                )}
                {selectedNotification.redirectUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleNavigateToRedirect(selectedNotification.redirectUrl!)}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Details
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsPage;
